/**
 * The sign-up questions' geometry, measured off the Sept 19 mockups.
 *
 * Mobile numbers come from the four `09.19 REF2 account onboarding mobile`
 * files, which are a 375 x 812 frame at 12.5013x; desktop from
 * `09.19 REF2 Account onboarding desktop.jpg`, a 1728 x 936 viewport at
 * 4.9392x.
 *
 * Type is sized by the **width of a known string**, not by eye and not by cap
 * height: each label was measured across in the mockup, the same string
 * measured in the browser in the real face, and the size solved from the two.
 * The frame is confirmed by the view toggle, which the mockups also draw: its
 * "My Schedule" measures 76.5px there, and the app renders it at 78.7px.
 *
 * One file rather than numbers sprinkled through the page, so that re-reading a
 * mockup means editing a table.
 */

export interface OnboardingScale {
  /** The card's corner, padding and the gap between its rows. */
  radius: number;
  pad: number;
  rowHeight: number;
  rowGap: number;
  rowRadius: number;
  /** Canela Deck, the question itself. */
  title: number;
  titleLine: number;
  helper: number;
  lead: number;
  label: number;
  description: number;
  emoji: number;
  /** The primary pill — Let's Go!, Next, Submit. */
  pill: number;
  pillText: number;
  /** Back and Skip, on the grass below the card. */
  foot: number;
  /** Step 1's own pieces. */
  lockup: number;
  body: number;
  fine: number;
  checkbox: number;
}

export const PHONE_SCALE: OnboardingScale = {
  radius: 22,
  pad: 22,
  rowHeight: 41,
  rowGap: 9,
  rowRadius: 999,
  title: 25.5,
  titleLine: 1.06,
  helper: 10.5,
  lead: 16,
  label: 12.5,
  description: 10,
  emoji: 20,
  pill: 36,
  pillText: 15,
  foot: 14,
  lockup: 127,
  body: 16,
  fine: 11.5,
  checkbox: 20,
};

export const DESK_SCALE: OnboardingScale = {
  radius: 28,
  pad: 26,
  rowHeight: 50,
  rowGap: 11,
  rowRadius: 999,
  title: 31,
  titleLine: 1.06,
  helper: 12.5,
  lead: 19,
  label: 15,
  description: 12,
  emoji: 24,
  pill: 42,
  pillText: 17,
  foot: 17,
  lockup: 186,
  body: 19,
  fine: 14,
  checkbox: 24,
};

/**
 * The card's width, in one expression rather than a breakpoint.
 *
 * 321px of a 375px phone and 471px of a 1728px laptop are the two mockups'
 * cards; between them the phone's width wins, because 27vw of an iPad is a
 * column of nothing. The 86vw ceiling keeps the phone's own margins.
 */
export const CARD_WIDTH = 'min(86vw, clamp(321px, 27.26vw, 720px))';
